import { useEffect, useState } from "react";
import FormField from "./FormField";
import Text from "./Text";
import Container from "./Container";
import { InputText } from "primereact/inputtext";
import Icon from "./Icon";
import { ProgressSpinner } from "primereact/progressspinner";
import useAuthHttp from "../hooks/useAuthHttp";
import { useQuery } from "@tanstack/react-query";
import { IFund } from "./FundSearchSelector";
import { shouldSearchListedFund } from "../commons/listed-fund-ticker";

export interface IStock {
    ticker_symbol: string;
    company_short_name: string;
}

interface IStockList {
    stock_list: IStock[];
}

export interface IStockSearchSelectorProps {
    selected: IStock|undefined;
    select: (currency: IStock|undefined) => void;
    onSelectFund?: (fund: IFund) => void;
    hasError?: boolean;
    error?: string;
}

const StockSearchSelector = (props: IStockSearchSelectorProps) => {
    const [searchQueryDebounce, setSearchQueryDebounce] = useState<string>("");
    const [searchQuery, setSearchQuery] = useState<string>("");

    const authHttp = useAuthHttp();
    const stockQuery = useQuery({
        queryKey: ["stock", searchQueryDebounce],
        queryFn: async () => {
            if (!searchQueryDebounce) {
                return { stock_list: [] };
            }
            
            return await authHttp.doGet<IStockList>("/stock?search=" + searchQueryDebounce);
        }
    });
    const stockCount = stockQuery.data?.stock_list?.length ?? 0;
    const searchFund = !!props.onSelectFund && shouldSearchListedFund(
        searchQueryDebounce,
        stockQuery.isPending ? -1 : stockCount
    );
    const fundQuery = useQuery({
        queryKey: ["fund-from-stock", searchQueryDebounce],
        enabled: searchFund,
        queryFn: async () => {
            if (!searchQueryDebounce) {
                return { if_list: [] as IFund[] };
            }
            return await authHttp.doGet<{ if_list: IFund[] }>("/inv_fund?search=" + searchQueryDebounce);
        }
    });
    const fundList = fundQuery.data?.if_list ?? [];
    const isLoading = stockQuery.isPending || (searchFund && fundQuery.isPending);
    useEffect(() => {
        const timeout = setTimeout(async () => {
            setSearchQueryDebounce(searchQuery);
        }, 1500);

        return () => clearTimeout(timeout);
    }, [searchQuery]);

    return (
        <FormField
            id="stockSearchSelector"
            label="Ações"
            hasError={props.hasError}
            error={props.error}
        >
            <Container relative>
                <InputText 
                    value={props.selected?.ticker_symbol ?? searchQuery}
                    className="w-full pr-5"
                    placeholder="Ação (PETR4, MGLU3). ETF tipo BRAX11 fica em Fundos"
                    invalid={props.hasError}
                    onChange={(e) => {
                        setSearchQuery(e.target.value);
                    }}
                />
                
                {
                    props.selected && <Icon 
                        name="pi-times"
                        className="absolute right-0 mr-1 p-2 text-red-500 bg-white"
                        onClick={() => {
                            props.select(undefined);
                            setSearchQuery("");
                        }}
                        style={{ transform: "translateY(-50%)", top: "50%" }}
                    />
                }
            </Container>
            {
                isLoading && (
                    <Container center flexColumn className="my-progress-spinner p-4" gap={2}>
                        <ProgressSpinner className="w-2rem h-auto" />
                        <Text>Carregando...</Text>
                    </Container>       
                )
            }

            {
                !isLoading && !props.selected && searchQuery && (!!stockQuery.data?.stock_list?.length || !!fundList.length) && (
                    <div className="bg-white w-full">
                        <ul className="list-none p-0 flex gap-2 flex-column">
                            {
                                stockQuery.data?.stock_list?.map(stock => {
                                    return (
                                        <li key={stock.ticker_symbol}>
                                            <a 
                                                className="cursor-pointer" 
                                                onClick={() => {
                                                    props.select(stock);
                                                    setSearchQuery("");
                                                }}
                                            >
                                                {stock.ticker_symbol}
                                            </a>
                                        </li>
                                    );
                                })
                            }
                            {
                                !!fundList.length && (
                                    <>
                                        <li className="text-sm text-600 pt-2">ETF / fundo listado — cadastre em Fundos</li>
                                        {fundList.map(fund => (
                                            <li key={`${fund.fund_type}-${fund.ticker_symbol}`}>
                                                <a
                                                    className="cursor-pointer"
                                                    onClick={() => {
                                                        props.onSelectFund?.(fund);
                                                        setSearchQuery("");
                                                    }}
                                                >
                                                    {fund.ticker_symbol} - {fund.company_short_name}
                                                </a>
                                            </li>
                                        ))}
                                    </>
                                )
                            }
                        </ul>
                    </div>
                )
            }
            {
                !isLoading && !props.selected && searchQuery && !stockCount && !fundList.length && !stockQuery.isPending && (
                    <Text className="text-sm text-600 mt-2">
                        Nenhuma ação encontrada. ETFs, FIIs e previdência ficam em Fundos / ETFs / Previdência.
                    </Text>
                )
            }
        </FormField>
    );
}

export default StockSearchSelector;